'use client'

import React, { useRef, useState, useLayoutEffect } from 'react'
import type { Page } from '@/payload-types'
import { CMSLink } from '@/components/Link'
import RichText from '@/components/RichText'
import { cn } from '@/utilities/ui'
import usePixelator from '@/components/Pixelator/usePixelator'
import { Pixelator } from '@/components/Pixelator'
import { Heading } from '@/components/ui/heading'
import patternPlain from './pattern-plain.png'
import patternHalf from './pattern-half.png'
import Image from 'next/image'
import { ShardArea } from '@/components/ShardArea'

export const HighImpactHero = HighImpactHeroWithAnimatedShards

export function HighImpactHeroWithFullAnimation({
  links,
  media,
  description,
  heading,
}: Page['hero']) {
  const { state, canvasRef, containerRef, canvasContainerRef, imageMeta, params } = usePixelator({
    imageUrl: media && typeof media === 'object' && media.url ? media.url : undefined,
  })

  return (
    <section className={cn('flex flex-col-reverse lg:flex-row', 'lg:min-h-[calc(100vh-150px)]')}>
      <div className={cn('flex-1', 'bg-olive-2 py-0')}>
        <div
          className={cn(
            'px-4',
            'container mx-auto',
            'lg:container-half lg:ml-auto lg:mr-0 lg:h-full',
          )}
        >
          <div className="lg:pr-[50px] h-full">
            <div className="relative flex-1 h-full flex flex-col justify-end items-start pb-[80px] lg:pt-[50px]">
              <Heading
                size="xl"
                className="lg:text-heading-h1 2xl:text-heading-xl text-balance mt-[20px] lg:mt-[60px] mb-[30px] md:mb-[60px] text-highlighter"
              >
                {heading}
              </Heading>
              {description && (
                <RichText
                  className={cn(
                    'overflow-hidden mb-[30px] md:mb-[60px]',
                    '[&_p]:md:!text-body-xl [&_p]:max-w-[600px] [&_p]:text-balance',
                    'prose-light',
                    'text-balance',
                  )}
                  data={description}
                />
              )}

              {Array.isArray(links) && links.length > 0 && (
                <ul className="flex flex-col md:flex-row gap-y-[10px] md:gap-x-[30px]">
                  {links.map(({ link }, i) => {
                    return (
                      <li key={i}>
                        <CMSLink {...link} />
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={cn('flex-1 bg-paper-1', 'bg-olive-2')}>
        <div
          className={cn(
            'px-4',
            'container mx-auto',
            'lg:container-half lg:ml-0 lg:mr-auto lg:h-full',
          )}
        >
          <div className={cn('flex h-full')}>
            {media && typeof media === 'object' && (
              <div
                className={cn(
                  'relative h-[200px] md:h-[300px] pt-[50px] pb-[30px] w-full lg:pt-[80px] lg:pb-[80px] flex justify-start items-end lg:h-full lg:w-full',
                )}
              >
                <Pixelator
                  state={state}
                  canvasContainerRef={canvasContainerRef}
                  containerRef={containerRef}
                  imageMeta={imageMeta}
                  canvasRef={canvasRef}
                  params={params}
                  className="lg:items-end"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export function HighImpactHeroWithStaticShards({ links, description, heading }: Page['hero']) {
  return (
    <div className="relative bg-olive-2 flex items-end xl:min-h-[calc(100vh-120px)]">
      <Image
        src={patternHalf}
        alt="pattern"
        className="hidden xl:block z-0 absolute top-0 left-0 w-full h-full object-cover"
        priority
      />
      <Image
        src={patternPlain}
        alt="pattern"
        className="block xl:hidden z-0 absolute top-0 left-0 w-full h-full object-cover"
        priority
      />
      <div className="flex flex-col w-full h-full xl:flex-row">
        <div className="xl:flex-1 h-full overflow-hidden container px-4 xl:pr-[50px] mx-auto xl:container-half xl:mr-0 xl:ml-auto">
          <div className="relative flex-1 h-full flex flex-col justify-end items-start md:min-h-[60vh] xl:min-h-[500px] pt-[30px] md:pt-[50px] pb-[30px] md:pb-[80px]">
            <Heading
              size="h1"
              className="text-balance mt-[20px] lg:mt-[60px] mb-[30px] md:mb-[60px] text-highlighter"
            >
              {heading}
            </Heading>
            {description && (
              <RichText
                className={cn(
                  'overflow-hidden mb-[30px] md:mb-[60px]',
                  '[&_p]:md:!text-body-xl [&_p]:max-w-[600px] [&_p]:text-balance',
                  'prose-light',
                  'text-balance',
                )}
                data={description}
              />
            )}

            {Array.isArray(links) && links.length > 0 && (
              <ul className="flex flex-col md:flex-row gap-y-[20px] md:gap-x-[30px]">
                {links.map(({ link }, i) => {
                  return (
                    <li key={i}>
                      <CMSLink {...link} />
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
        <div className="xl:flex-1 h-full xl:container-half xl:mr-auto overflow-hidden" />
      </div>
    </div>
  )
}

export function HighImpactHeroWithAnimatedShards({ links, description, heading }: Page['hero']) {
  const heightClassName = 'h-[600px] md:h-[calc(100vh-120px)]'
  const containerRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const [ignoreRegions, setIgnoreRegions] = useState<
    Array<{ rowStart: number; rowEnd: number; colStart: number; colEnd: number }>
  >([])

  const cellSize = 10 // Keep in sync with CamoShardArea default or prop

  useLayoutEffect(() => {
    function calc() {
      if (!containerRef.current || !overlayRef.current) return
      const contRect = containerRef.current.getBoundingClientRect()
      const ovRect = overlayRef.current.getBoundingClientRect()
      const top = ovRect.top - contRect.top
      const left = ovRect.left - contRect.left
      const bottom = top + ovRect.height
      const right = left + ovRect.width
      const rowStart = Math.floor(top / cellSize)
      const rowEnd = Math.ceil(bottom / cellSize) - 1
      const colStart = Math.floor(left / cellSize)
      const colEnd = Math.ceil(right / cellSize) - 1
      setIgnoreRegions([{ rowStart, rowEnd, colStart, colEnd }])
    }
    calc()
    const ro = new ResizeObserver(calc)
    if (containerRef.current) ro.observe(containerRef.current)
    if (overlayRef.current) ro.observe(overlayRef.current)
    return () => ro.disconnect()
  }, [])

  return (
    <div
      className={cn('relative w-full bg-olive-2 flex items-end overflow-hidden', heightClassName)}
    >
      <div ref={containerRef} className="absolute top-0 bottom-0 left-0 right-0 w-full h-full">
        <ShardArea
          className="w-full h-full"
          cellSize={cellSize}
          regionSize={20}
          shardPadding={4}
          bleed={50}
          ignorePadding={2}
          ignoreRegions={ignoreRegions}
        />
      </div>
      <section
        className={cn(
          'z-10 flex flex-col-reverse lg:flex-row',
          'w-full h-full pointer-events-none',
        )}
      >
        {/* Left Section */}
        <div className={cn('flex-1')}>
          <div
            className={cn(
              'px-4',
              'container mx-auto',
              'lg:container-half lg:ml-auto lg:mr-0 lg:h-full',
              'flex flex-col justify-end',
              heightClassName,
            )}
          >
            <div className="lg:pr-[50px] pt-[0px] md:pt-[30px] lg:pt-[80px] pb-[50px] md:pb-[80px]">
              <div className="pointer-events-auto" ref={overlayRef}>
                <Heading size="h1" className="text-balance mb-[30px] md:mb-[60px] text-highlighter">
                  {heading}
                </Heading>
                {description && (
                  <RichText
                    className={cn(
                      'overflow-hidden mb-[30px] md:mb-[60px]',
                      '[&_p]:md:!text-body-xl [&_p]:max-w-[600px] [&_p]:text-balance',
                      'prose-light',
                      'text-balance',
                    )}
                    data={description}
                  />
                )}

                {Array.isArray(links) && links.length > 0 && (
                  <ul className="flex flex-col md:flex-row gap-y-[20px] md:gap-x-[30px]">
                    {links.map(({ link }, i) => {
                      return (
                        <li key={i}>
                          <CMSLink {...link} />
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Right Section (Full Bleed) */}
        <div className={cn('relative flex-1 overflow-hidden')}>
          <div
            className={cn('px-0', 'lg:px-4 lg:container-half lg:ml-0 lg:mr-auto lg:h-full')}
          ></div>
        </div>
      </section>
    </div>
  )
}
